import classnames from 'classnames';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { createVar } from 'style/utils';

const customBackground = createVar();
const customColor = createVar();

const Root = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  isolation: isolate;
  gap: var(--unit);
  align-items: center;
  justify-content: center;
  padding: var(--unit) var(--double-unit);
  min-height: calc(var(--unit) * 5);
  min-width: calc(var(--unit) * 5);
  border-radius: var(--quadruple-unit);
  white-space: nowrap;
  background: rgba(var(--c-rgb-neutral-1000), 0.1);
  color: var(--c-neutral-1000);
  font: var(--t-bold) var(--t-16);

  &.small,
  &.smaller {
    gap: var(--half-unit);
  }
  &.small {
    padding: var(--half-unit) var(--intermediate-unit);
    min-height: var(--quadruple-unit);
    min-width: var(--quadruple-unit);
  }
  &.smaller {
    padding: 0 var(--unit);
    min-height: var(--triple-unit);
    min-width: var(--triple-unit);
    font: var(--t-bold) var(--t-12);
  }
  &.outlined {
    --border-color: currentColor;
    box-shadow: inset 0px 0px 0px 1px var(--border-color); /** inner border */
    background: var(--c-neutral-100);
  }
  &.brand {
    background: var(--c-brand-600);
  }
  &.red {
    background: rgba(var(--c-static-rgb-red-300), 0.1);
    color: var(--c-static-red-300);
  }
  &.green {
    background: rgba(var(--c-rgb-green-600), 0.1);
    color: var(--c-green-600);
  }
  &.gray {
    color: var(--c-neutral-600);
  }
  &.yellow {
    background: var(--c-yellow-300);
    color: var(--c-neutral-1000);
  }
  &.middleGrey {
    background: var(--c-neutral-600);
    color: var(--c-neutral-100);
  }
  &.black {
    color: var(--c-neutral-100);
    &:not(.outlined) {
      background: var(--c-neutral-1000);
    }
    &.outlined {
      color: var(--c-neutral-1000);
      background: transparent;
    }
  }
  &.opa {
    color: var(--c-neutral-1000);
    &:not(.outlined) {
      background: rgba(var(--c-rgb-neutral-1000), 0.16);
      border: solid rgba(var(--c-rgb-neutral-1000), 0.25) 1px;
    }
    &.outlined {
      color: var(--c-neutral-100);
      background: rgba(var(--c-rgb-neutral-100), 0.16);
    }
  }
  &.glass {
    color: var(--c-neutral-1000);
    backdrop-filter: blur(4px);
    background-color: rgba(var(--c-rgb-neutral-1000), 0.1);
  }
  &.custom {
    color: var(${customColor});
    &:not(.outlined) {
      background: var(${customBackground});
    }
    &.outlined {
      color: var(${customColor});
      background: var(${customBackground});
    }
  }
  &.uppercased {
    text-transform: uppercase;
  }
`;

type Props = {
  children: ReactNode;
  color?:
    | 'brand'
    | 'red'
    | 'middleGrey'
    | 'gray'
    | 'black'
    | 'green'
    | 'yellow'
    | 'opa'
    | 'glass';
  custom?: { color?: string; background?: string };
  size?: 'small' | 'smaller';
  outlined?: boolean;
};
export const Chip = ({ children, color, size, outlined, custom }: Props) => {
  return (
    <Root
      className={classnames(color, { custom }, size, {
        outlined,
      })}
      style={{
        [customColor]: custom?.color,
        [customBackground]: custom?.background,
      }}
    >
      {children}
    </Root>
  );
};
